import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { DrugTagDTO, DrugTagRequestDTO } from '../../dto/DrugTagDTO';
import { createDrugTag, deleteDrugTag, getDrugTags, updateDrugTag } from '../../services/DrugTagController';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import LoadTable, { LoadTableColumn, LoadTableRow } from '../../components/novarium/LoadTable';
import NvTooltips from '../../components/novarium/NvTooltips';
import { AddRowButton, FieldError } from '../../components/novarium/NvTableParts';
import { confirmDelete } from '../../components/ConfirmDelete/confirmDelete';

/** Largo de la columna del nombre. */
const NAME_MAX_LENGTH = 255;

type FieldName = 'name';
type FieldErrors = Partial<Record<FieldName, string>>;

/** Campo del request → campo del formulario, para los marcadores de VALIDATION_ERROR y del duplicado. */
const REQUEST_FIELDS: Record<string, FieldName> = { name: 'name' };

type Row = LoadTableRow & { tag: DrugTagDTO };

const rowKeyOf = (tag: DrugTagDTO) => `t-${tag.drugTag}`;

function drugCountLabel(count: number): string {
    return count === 1 ? '1 medicamento' : `${count} medicamentos`;
}

/**
 * Etiquetas de medicamentos (ej. "Alto costo"): se asignan al medicamento desde Artículos › Medicamentos y filtran
 * el reporte de medicación prescripta. Input table de Novarium: alta y edición en la primera fila, cada acción se
 * guarda en el momento. Una etiqueta en uso no se puede eliminar.
 */
export default function DrugTagView() {
    const notify = useNotify();

    const [tags, setTags] = useState<DrugTagDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [editing, setEditing] = useState<DrugTagDTO | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);
    const nameRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setTags(await getDrugTags());
        } catch {
            notify.error('No se pudieron cargar las etiquetas de medicamentos. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    function resetForm() {
        setName('');
        setEditing(null);
        setErrors({});
    }

    function startEdit(tag: DrugTagDTO) {
        setName(tag.name);
        setEditing(tag);
        setErrors({});
        nameRef.current?.focus();
    }

    function cancelEdit() {
        resetForm();
        nameRef.current?.focus();
    }

    async function handleSubmit() {
        if (saving) return;
        if (!name.trim()) {
            setErrors({ name: 'Completá el nombre' });
            nameRef.current?.focus();
            return;
        }

        const body: DrugTagRequestDTO = { name: name.trim() };
        setSaving(true);
        try {
            if (editing) {
                const updated = await updateDrugTag(editing.drugTag, body);
                setTags(current => current.map(t => t.drugTag === updated.drugTag ? updated : t));
                notify.success('Cambios guardados');
            } else {
                const created = await createDrugTag(body);
                // La fila nueva va primera
                setTags(current => [created, ...current]);
                notify.success('Etiqueta creada');
            }
            resetForm();
            nameRef.current?.focus();
        } catch (error: unknown) {
            handleSaveError(error);
        } finally {
            setSaving(false);
        }
    }

    function handleSaveError(error: unknown) {
        const apiError = parseApiError(error);
        const fieldErrors: FieldErrors = {};
        for (const detail of apiError.details ?? []) {
            const field = REQUEST_FIELDS[detail.field];
            if (field) fieldErrors[field] = detail.message;
        }
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            nameRef.current?.focus();
            return;
        }
        if (apiError.status === 404) {
            // Ya no está (otra sede u otra pestaña): se recarga la lista
            notify.error(apiError.message);
            resetForm();
            load();
            return;
        }
        const isBusinessError = apiError.status >= 400 && apiError.status < 500;
        const fallback = editing
            ? 'No se pudieron guardar los cambios. Intentá de nuevo.'
            : 'No se pudo crear la etiqueta. Intentá de nuevo.';
        notify.error(isBusinessError ? apiError.message : fallback);
    }

    function askDelete(tag: DrugTagDTO) {
        confirmDelete({
            header: '¿Eliminar etiqueta?',
            message: `Se va a eliminar la etiqueta «${tag.name}».`,
            onAccept: () => { handleDelete(tag); },
        });
    }

    async function handleDelete(tag: DrugTagDTO) {
        try {
            await deleteDrugTag(tag.drugTag);
            setTags(current => current.filter(t => t.drugTag !== tag.drugTag));
            if (editing?.drugTag === tag.drugTag) resetForm();
            notify.success('Etiqueta eliminada');
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError ? apiError.message : 'No se pudo eliminar la etiqueta. Intentá de nuevo.');
            if (apiError.status === 404 || apiError.status === 409) load();
        }
    }

    /** Enter guarda; Esc descarta la edición. */
    function handleFormKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
        } else if (event.key === 'Escape' && editing) {
            event.preventDefault();
            cancelEdit();
        }
    }

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'name',
            header: 'Nombre',
            className: 'nv-col-item',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleFormKeyDown}>
                    <InputText
                        id="drug-tag-name"
                        ref={nameRef}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setErrors({});
                        }}
                        maxLength={NAME_MAX_LENGTH}
                        placeholder="Ej.: Alto costo"
                        className={classNames('nv-in-cell', { 'p-invalid': errors.name, 'p-filled': name })}
                        aria-label="Nombre"
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'drug-tag-name-error' : undefined}
                    />
                    <FieldError id="drug-tag-name-error" message={errors.name} />
                </div>
            ),
            body: ({ tag }) => tag.name,
        },
        {
            key: 'drugCount',
            header: 'Medicamentos',
            align: 'center',
            className: 'nv-col-qty',
            body: ({ tag }) => tag.drugCount,
        },
    ];

    const addAction = editing ? (
        <span className="nv-cell-actions">
            <Button
                type="button"
                text
                className="nv-icon nv-tip"
                icon="pi pi-check"
                aria-label="Confirmar cambios"
                data-pr-tooltip="Confirmar cambios"
                loading={saving}
                onClick={handleSubmit}
            />
            <Button
                type="button"
                text
                className="nv-icon nv-tip"
                icon="pi pi-times"
                aria-label="Cancelar cambios"
                data-pr-tooltip="Cancelar cambios"
                disabled={saving}
                onClick={cancelEdit}
            />
        </span>
    ) : (
        <AddRowButton label="Agregar" loading={saving} onClick={handleSubmit} />
    );

    const rows: Row[] = tags.map(tag => ({ rowKey: rowKeyOf(tag), tag }));

    return (
        <div className="nv-scope nv-page">
            <NvTooltips />
            <ConfirmDialog />
            <div className="nv-page-head">
                <h1 className="nv-h1">Etiquetas de medicamentos</h1>
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    Se asignan desde Artículos › Medicamentos y aplican a todas las presentaciones del medicamento.
                </p>
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    Una etiqueta asignada a medicamentos o que limita lo que prescribe una especialidad no se puede eliminar.
                </p>
            </div>

            {loading ? (
                <div className="nv-loading" role="status">
                    <ProgressSpinner strokeWidth="4" style={{ width: 32, height: 32 }} />
                    <span>Cargando…</span>
                </div>
            ) : (
                <LoadTable
                    rows={rows}
                    columns={columns}
                    addAction={addAction}
                    rowActions={({ tag }) => (
                        <span className="nv-cell-actions">
                            <Button
                                type="button"
                                text
                                className="nv-icon nv-tip"
                                icon="pi pi-pencil"
                                aria-label={`Editar ${tag.name}`}
                                data-pr-tooltip="Editar"
                                disabled={saving}
                                onClick={() => startEdit(tag)}
                            />
                            {/* En uso: se previene en vez de avisar después. El tooltip va en el span porque un botón
                                deshabilitado no recibe el mouse. */}
                            <span
                                className={tag.drugCount > 0 ? 'nv-tip nv-disabled-tip' : 'nv-tip'}
                                data-pr-tooltip={tag.drugCount > 0
                                    ? `Está asignada a ${drugCountLabel(tag.drugCount)}: quitala en Medicamentos para eliminarla`
                                    : 'Eliminar'}
                                tabIndex={tag.drugCount > 0 ? 0 : undefined}
                            >
                                <Button
                                    type="button"
                                    text
                                    className="nv-icon"
                                    icon="pi pi-trash"
                                    aria-label={`Eliminar ${tag.name}`}
                                    disabled={saving || tag.drugCount > 0}
                                    onClick={() => askDelete(tag)}
                                />
                            </span>
                        </span>
                    )}
                    emptyMessage="Todavía no hay etiquetas de medicamentos cargadas."
                    editingRowKey={editing ? rowKeyOf(editing) : null}
                />
            )}
        </div>
    );
}
